import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightUpLine, RiMapPinLine, RiPhoneLine } from 'react-icons/ri';
import { FaDownload } from 'react-icons/fa';

import { Link } from 'react-router-dom';

import { useInstall } from '../../../context/InstallContext';
import { usePublicSettings } from '../../../context/PublicSettingsContext.js';
import { useStoreLogo } from '../../../hooks/useStoreLogo.js';
import SocialLinks from '../../common/SocialLinks/SocialLinks.jsx';
import { subscribeNewsletter } from '../../../api/subscriberApi';
import './Footer.css';
import Button from '../../UI/Button/Button';
import LanguageContext from '../../../context/LanguageContext.js';
import { useStorePathPrefix } from '../../../lib/storefront/storePath.js';
import { hasPublicAddress } from '../../../utils/formatPublicAddress.js';

const Footer = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const storePrefix = useStorePathPrefix();
    const [email, setEmail] = useState('');
    const [subscribeStatus, setSubscribeStatus] = useState({ type: '', message: '' });
    const [subscribing, setSubscribing] = useState(false);
    const { canInstall, isIOS, isInstalled, promptInstall } = useInstall();
    const { publicSettings } = usePublicSettings();
    const { src: logoSrc, alt: logoAlt, onError: onLogoError, isRemote } = useStoreLogo();

    const getLangPrefix = () => `${storePrefix}/${currentLanguage}`;
    const isPageEnabled = (slug) => {
        if (!publicSettings?.storefrontPages || typeof publicSettings.storefrontPages !== 'object') return true;
        const entry = publicSettings.storefrontPages[slug];
        if (!entry || typeof entry !== 'object') return true;
        return entry.enabled !== false;
    };

    const showWishlistInFooter = isPageEnabled('wishlist') && (process.env.REACT_APP_SHOW_WISHLIST_FOOTER || 'false').toLowerCase() === 'true';
    const showReservationInFooter = isPageEnabled('reservation') && (process.env.REACT_APP_SHOW_RESERVATION_FOOTER || 'false').toLowerCase() === 'true';
    // Blog link is disabled below; re-enable this together with it.
    // const showBlogInFooter = isPageEnabled('blog') && (process.env.REACT_APP_SHOW_BLOG_FOOTER || 'false').toLowerCase() === 'true';

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setSubscribing(true);
        setSubscribeStatus({ type: '', message: '' });

        try {
            const result = await subscribeNewsletter(email);
            setSubscribeStatus({ type: 'success', message: result.message || t('footer.subscribeSuccess') });
            setEmail('');
        } catch (error) {
            setSubscribeStatus({ type: 'error', message: error.message || t('footer.subscribeFailed') });
        } finally {
            setSubscribing(false);
            setTimeout(() => setSubscribeStatus({ type: '', message: '' }), 5000);
        }
    };

    return (
        <footer className="footer section">
            <div className="footer-container container grid" dir={dir()}>
                <div className="footer-content footer-content--brand">
                    <Link to={getLangPrefix()} className="footer-logo">
                        <img
                            src={logoSrc}
                            className={`footer-logo-icon${isRemote ? '' : ' footer-logo-icon--brand'}`}
                            alt={logoAlt}
                            onError={onLogoError}
                            decoding="async"
                        />
                    </Link>
                    <p className="footer-description">
                        {t('footer.description')}
                    </p>

                    {isPageEnabled('subscribe') && (
                        <>
                            <h3 className="footer-title footer-title--subscribe">
                                {t('footer.subscribeTitle')}
                            </h3>

                            <form className="footer-subscribe" onSubmit={handleSubscribe}>
                                <input
                                    type="email"
                                    placeholder={t('footer.emailPlaceholder')}
                                    className="footer-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={subscribing}
                                    required
                                    aria-label={t('footer.emailPlaceholder')}
                                />

                                <Button
                                    type="submit"
                                    flex
                                    disabled={subscribing || !email}
                                >
                                    {subscribing ? t('footer.subscribing') : t('footer.subscribe')}
                                    <RiArrowRightUpLine className="button-icon" />
                                </Button>
                            </form>
                            {subscribeStatus.message && (
                                <p className={`footer-subscribe-msg footer-subscribe-msg--${subscribeStatus.type}`}>
                                    {subscribeStatus.message}
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="footer-content">
                    <h3 className="footer-title">
                        {t('footer.quickLinks')}
                    </h3>

                    <ul className="footer-data">
                        <li className="footer-information">
                            <Link to={`${getLangPrefix()}/menu`} className="footer-list-link">{t('nav.menu')}</Link>
                        </li>
                        {isPageEnabled('about') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/about`} className="footer-list-link">{t('nav.about')}</Link>
                            </li>
                        )}
                        <li className="footer-information">
                            <Link to={`${getLangPrefix()}#popular-dishes`} className="footer-list-link">
                                {t('nav.popularDishes')}
                            </Link>
                        </li>
                        {isPageEnabled('contact') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/contact`} className="footer-list-link">{t('nav.contact')}</Link>
                            </li>
                        )}
                        {isPageEnabled('subscribe') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/subscribe`} className="footer-list-link">{t('nav.subscribe')}</Link>
                            </li>
                        )}
                        {isPageEnabled('faq') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/faq`} className="footer-list-link">{t('nav.faq')}</Link>
                            </li>
                        )}
                        {isPageEnabled('events') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/events`} className="footer-list-link">{t('nav.events')}</Link>
                            </li>
                        )}
                        {isPageEnabled('gallery') && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/gallery`} className="footer-list-link">{t('nav.gallery')}</Link>
                            </li>
                        )}
                        {showReservationInFooter && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/reservation`} className='footer-list-link'>{t('nav.reservation')}</Link>
                            </li>
                        )}
                        {/*{showBlogInFooter && (*/}
                        {/*    <li className="footer-information">*/}
                        {/*        <Link to={`${getLangPrefix()}/blog`} className='footer-list-link'>{t('nav.blog')}</Link>*/}
                        {/*    </li>*/}
                        {/*)}*/}
                        {showWishlistInFooter && (
                            <li className="footer-information">
                                <Link to={`${getLangPrefix()}/wishlist`} className='footer-list-link'>{t('nav.wishlist')}</Link>
                            </li>
                        )}
                        {(canInstall || isIOS) && !isInstalled && (
                            <li className="footer-information">
                                <button
                                    className="footer-list-link footer-install-btn"
                                    onClick={() => canInstall ? promptInstall() : alert(t('installPrompt.iosGuide'))}
                                >
                                    <FaDownload className="footer-install-icon" />
                                    {t('installPrompt.installApp')}
                                </button>
                            </li>
                        )}
                    </ul>
                </div>

                <div className="footer-content">
                    <h3 className="footer-title">
                        {t('footer.contactUs')}
                    </h3>

                    <ul className="footer-data">
                        <li className="footer-information footer-contact-item">
                            <RiPhoneLine className="footer-contact-icon" />
                            <span>{publicSettings?.phoneNumber || t('contact.phoneNotConfigured')}</span>
                        </li>

                        <li className="footer-information footer-contact-item footer-contact-item--address">
                            <RiMapPinLine className="footer-contact-icon" />
                            <span>
                                {hasPublicAddress(publicSettings?.address) ? (
                                    <>
                                        {publicSettings.address.street && <>{publicSettings.address.street}<br /></>}
                                        {publicSettings.address.city && <>{publicSettings.address.city}</>}
                                        {publicSettings.address.state && <>, {publicSettings.address.state}</>}
                                        {publicSettings.address.zipCode && <> {publicSettings.address.zipCode}<br /></>}
                                        {publicSettings.address.country && <>{publicSettings.address.country}</>}
                                    </>
                                ) : (
                                    t('contact.addressNotConfigured')
                                )}
                            </span>
                        </li>
                        <SocialLinks
                            socialLinks={publicSettings?.socialLinks}
                            className="footer-social"
                            linkClassName="footer-social-link"
                        />
                    </ul>
                </div>
            </div>

            <div
                className="footer-bottom container"
                dir={dir()}
            >
                <p className="footer-copy">
                    {t('footer.copyright')}
                </p>

                <div className="footer-bottom-right">
                    {isPageEnabled('privacy-policy') && (
                        <Link to={`${getLangPrefix()}/privacy-policy`} className="footer-legal-link">
                            {t('footer.privacyPolicy')}
                        </Link>
                    )}

                    {isPageEnabled('terms-of-service') && (
                        <Link to={`${getLangPrefix()}/terms-of-service`} className="footer-legal-link">
                            {t('footer.termsOfService')}
                        </Link>
                    )}
                </div>
            </div>
        </footer>
    );
};

export default Footer;
